/* eslint-disable max-lines-per-function */
import { useCallback, useMemo } from 'react';

import {
   AppointmentSlotSchedule,
   AppointmentSlotScheduleQuery,
   AppointmentTimeSlotOccupiedFault,
   AppointmentUpdateTimeSlotCommand,
   AvailabilitySimple,
   AvailabilitySimpleQuery,
} from '@dreryk/egabinet-appointment-frontend-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';
import { VisitForSchedule, VisitForScheduleQuery } from '@dreryk/egabinet-visit-frontend-api';
import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import { TimeSlot } from '@dreryk/time-slot-common-api';

import { useCqrsCommand, useCqrsSearch } from '@dreryk/command-query-react-common';
import {
   BackgroundSlot,
   HoverSlot,
   OnEventUpdate,
   TimetableEvent,
   TimetableHoverConfiguration,
} from '@dreryk/react-calendar';
import { dispatchSnackbarNegativeEvent } from '@dreryk/react-components';
import { Range, Slot } from '@dreryk/watermelon';

import { protectFeature } from '~libs.shared.system';

import {
   AppointmentBackgroundSlotData,
   AppointmentEventSlotData,
   AppointmentHoverSlotData,
   AppointmentSchedule,
} from './AppointmentsTimetable.types';
import {
   allowedEventUpdateSelectorOnlyWithinOneDateForSamePractitioner,
   areSlotsDatesEqual,
   createTimeSlotFromAvailability,
   getEventOrder,
   isOverlappable,
} from './AppointmentsTimetable.utils';
import { translationKeys } from './translations';

type UseAppointmentSchedulesOptions = {
   dateRange: Range<Date>;
   practitionerIds: PractitionerId[];
};

type UseAppointmentSchedulesReturnFn = {
   appointmentSchedules: AppointmentSchedule[] | null;
   appointmentSchedulesFetching: boolean;
   refetchAppointmentSchedules: VoidFunction;
};

export const useAppointmentSchedules = (options: UseAppointmentSchedulesOptions): UseAppointmentSchedulesReturnFn => {
   const {
      data: appointmentSlotSchedules,
      fetching: appointmentSlotSchedulesFetching,
      refetch: refetchAppointmentSlotSchedule,
   } = useCqrsSearch<AppointmentSlotSchedule>(() => {
      const query = new AppointmentSlotScheduleQuery();
      query.dateRange = options.dateRange;
      query.practitionerIds = Slot.of(options.practitionerIds);
      return query;
   });

   const {
      data: availabilities,
      fetching: availabilitiesFetching,
      refetch: refetchAvailabilities,
   } = useCqrsSearch<AvailabilitySimple>(() => {
      const query = new AvailabilitySimpleQuery();
      query.dateRange = options.dateRange;
      query.practitionerIds = options.practitionerIds;
      return query;
   });

   const {
      data: visitForSchedule,
      fetching: visitForScheduleFetching,
      refetch: refetchVisitForSchedule,
   } = useCqrsSearch<VisitForSchedule>(() => {
      const query = new VisitForScheduleQuery();
      query.plannedDateRange = options.dateRange;
      query.practitionerIds = Slot.of(options.practitionerIds);
      return query;
   });

   const refetchAppointmentSchedules = useCallback(
      async () => Promise.all([refetchAppointmentSlotSchedule(), refetchVisitForSchedule(), refetchAvailabilities()]),
      [refetchAppointmentSlotSchedule, refetchVisitForSchedule, refetchAvailabilities]
   );

   return useMemo(() => {
      const visitMap = new Map(visitForSchedule?.map((visit) => [visit.appointmentId, visit]));
      const backgroundSlotsMap = (availabilities ?? []).reduce((map, availability) => {
         const id = availability.practitioner.id.asNumber();
         const slots = map.get(id) ?? [];
         slots.push(createTimeSlotFromAvailability(availability));
         return map.set(id, slots);
      }, new Map<number, TimeSlot[]>());

      return {
         appointmentSchedules:
            appointmentSlotSchedules && visitForSchedule && availabilities
               ? appointmentSlotSchedules.map((appointmentSlotSchedule) => ({
                    resourceId: appointmentSlotSchedule.practitionerId,
                    backgroundSlots: backgroundSlotsMap.get(appointmentSlotSchedule.practitionerId.asNumber()),
                    hoverSlots: appointmentSlotSchedule.freeSlots,
                    eventsSlots: appointmentSlotSchedule.appointmentSlots.map(
                       (slot): AppointmentEventSlotData => ({
                          ...slot,
                          visit: visitMap.get(slot.appointmentId),
                       })
                    ),
                 }))
               : null,
         appointmentSchedulesFetching:
            appointmentSlotSchedulesFetching || visitForScheduleFetching || availabilitiesFetching,
         refetchAppointmentSchedules: refetchAppointmentSchedules,
      };
   }, [
      appointmentSlotSchedules,
      appointmentSlotSchedulesFetching,
      availabilities,
      availabilitiesFetching,
      refetchAppointmentSchedules,
      visitForSchedule,
      visitForScheduleFetching,
   ]);
};

export const useEvents = (
   appointmentSchedules: AppointmentSchedule[] | null
): TimetableEvent<AppointmentEventSlotData>[] | null => {
   return useMemo(() => {
      if (!appointmentSchedules) {
         return null;
      }

      return appointmentSchedules.reduce<TimetableEvent<AppointmentEventSlotData>[]>((result, schedule) => {
         schedule.eventsSlots.forEach((eventSlot) => {
            result.push({
               id: eventSlot.appointmentId,
               start: eventSlot.timeSlot.getBeginDate(),
               end: eventSlot.timeSlot.getEndDate(),
               data: eventSlot,
               resourceId: schedule.resourceId.toString(),
               order: getEventOrder(eventSlot.status),
               overlappable: isOverlappable(eventSlot.status),
            });
         });

         return result;
      }, []);
   }, [appointmentSchedules]);
};

export const useBackgroundSlots = (
   appointmentSchedules: AppointmentSchedule[] | null
): BackgroundSlot<AppointmentBackgroundSlotData>[] | undefined => {
   return useMemo((): BackgroundSlot<AppointmentBackgroundSlotData>[] | undefined => {
      if (!appointmentSchedules) {
         return undefined;
      }

      return appointmentSchedules.reduce<BackgroundSlot<AppointmentBackgroundSlotData>[]>((result, schedule) => {
         schedule.backgroundSlots?.forEach((backgroundSlot) => {
            result.push({
               start: backgroundSlot.getBeginDate(),
               end: backgroundSlot.getEndDate(),
               data: backgroundSlot,
               resourceId: schedule.resourceId.toString(),
            });
         });

         return result;
      }, []);
   }, [appointmentSchedules]);
};

export const useHoverSlots = (
   appointmentSchedules: AppointmentSchedule[] | null
): HoverSlot<AppointmentHoverSlotData>[] | undefined => {
   const creatingAppointmentsAvailable = protectFeature('creatingAppointments');

   return useMemo(() => {
      if (!appointmentSchedules || !creatingAppointmentsAvailable) {
         return undefined;
      }

      return appointmentSchedules.reduce<HoverSlot<AppointmentHoverSlotData>[]>((result, schedule) => {
         schedule.hoverSlots?.forEach((slot) => {
            result.push({
               start: slot.timeSlot.getBeginDate(),
               end: slot.timeSlot.getEndDate(),
               data: slot,
               resourceId: schedule.resourceId.toString(),
            });
         });

         return result;
      }, []);
   }, [appointmentSchedules, creatingAppointmentsAvailable]);
};

export const useHoverConfiguration = (
   nextAvailableTimeSlot: AppointmentHoverSlotData | undefined
): TimetableHoverConfiguration<AppointmentHoverSlotData> => {
   return useMemo<TimetableHoverConfiguration<AppointmentHoverSlotData>>(
      () => ({
         visibilitySelector: (event) => {
            if (!nextAvailableTimeSlot || !areSlotsDatesEqual(event.data, nextAvailableTimeSlot)) {
               return 'hover';
            }

            return 'focus';
         },
      }),
      [nextAvailableTimeSlot]
   );
};

export const useEventUpdater = (onEventUpdate: VoidFunction): OnEventUpdate<AppointmentEventSlotData> => {
   const creatingAppointmentsAvailable = protectFeature('creatingAppointments');

   const { execute: updateAppointment } = useCqrsCommand<
      void,
      TimetableEvent<AppointmentEventSlotData>,
      AppointmentTimeSlotOccupiedFault
   >(
      (parameters) => {
         const command = new AppointmentUpdateTimeSlotCommand();
         command.id = AppointmentId.of(parameters.data.appointmentId);
         command.timeSlot = TimeSlot.of(parameters.start, parameters.end);
         return command;
      },
      {
         onSuccess: onEventUpdate,
         onFault: {
            'egabinet-appointment-frontend-api:AppointmentTimeSlotOccupiedFault': () => {
               dispatchSnackbarNegativeEvent(translationKeys.timeSlotChangeTaken);
               onEventUpdate();
            },
         },
      }
   );

   return useMemo<OnEventUpdate<AppointmentEventSlotData>>(
      () => ({
         disableEventFromUpdateSelector: (event) => event.data.status === 'REGISTERED' && creatingAppointmentsAvailable,
         allowedEventUpdateSelector: allowedEventUpdateSelectorOnlyWithinOneDateForSamePractitioner,
         handleEventUpdate: updateAppointment,
      }),
      [updateAppointment, creatingAppointmentsAvailable]
   );
};
